import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  MessageSquare,
  Loader2,
  User,
  Trash2,
  Mail,
  MessageCircle,
  ChevronRight
} from 'lucide-react';
import { dmAPI, messagesAPI } from '../services/api';
import BottomNav from '../components/BottomNav';
import notificationSound from '../utils/notificationSound';
import { LinkifyText } from '../utils/linkify';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const MessagesPage = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const prevInboxCount = useRef(0);
  const isFirstLoad = useRef(true);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conversations');
  const [deleteMessageId, setDeleteMessageId] = useState(null);
  const [deleteConvoUserId, setDeleteConvoUserId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedMessage, setExpandedMessage] = useState(null);

  useEffect(() => {
    loadData();
    
    // Poll for new messages every 30 seconds
    const pollInterval = setInterval(() => {
      loadData();
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, []);

  const loadData = async () => {
    try {
      const [convos, inbox] = await Promise.all([
        dmAPI.getConversations(),
        messagesAPI.getInbox()
      ]);
      setConversations(convos);
      
      // Play sound if new inbox messages arrived (not on first load)
      if (!isFirstLoad.current && inbox.length > prevInboxCount.current) {
        notificationSound.playMessageSound();
      }
      prevInboxCount.current = inbox.length;
      isFirstLoad.current = false;
      
      setInboxMessages(inbox);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const handleDeleteMessage = async () => {
    if (!deleteMessageId) return;
    
    setDeleting(true);
    try {
      await messagesAPI.delete(deleteMessageId);
      setInboxMessages(prev => prev.filter(m => m.id !== deleteMessageId));
      setExpandedMessage(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    } finally {
      setDeleting(false);
      setDeleteMessageId(null);
    }
  };

  const handleDeleteConversation = async () => {
    if (!deleteConvoUserId) return;
    
    setDeleting(true);
    try {
      await dmAPI.deleteConversation(deleteConvoUserId);
      setConversations(prev => prev.filter(c => c.oderId !== deleteConvoUserId));
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Failed to delete conversation');
    } finally {
      setDeleting(false);
      setDeleteConvoUserId(null);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await messagesAPI.markAsRead(messageId);
      setInboxMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const toggleExpandMessage = (message) => {
    if (expandedMessage?.id === message.id) {
      setExpandedMessage(null);
    } else {
      setExpandedMessage(message);
      if (!message.read) {
        handleMarkAsRead(message.id);
      }
    }
  };

  const unreadInboxCount = inboxMessages.filter(m => !m.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-xl">Messages</h1>
            <p className="text-sm text-tan-400">Stay connected</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex mt-4 gap-2">
          <button
            onClick={() => setActiveTab('conversations')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'conversations'
                ? 'bg-white text-navy-900'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <MessageCircle className="w-4 h-4 inline mr-2" />
            Chats
          </button>
          <button
            onClick={() => setActiveTab('inbox')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors relative ${
              activeTab === 'inbox'
                ? 'bg-white text-navy-900'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Inbox
            {unreadInboxCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center font-bold px-1">
                {unreadInboxCount > 9 ? '9+' : unreadInboxCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {activeTab === 'conversations' ? (
          // Conversations Tab
          conversations.length === 0 ? (
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg font-medium">No conversations yet</p>
                <p className="text-gray-400 text-sm mt-2">
                  Start a conversation from a member&apos;s profile
                </p>
              </CardContent>
            </Card>
          ) : (
            conversations.map((convo) => (
              <Card
                key={convo.oderId}
                className={`border-0 shadow-md rounded-xl transition-all hover:shadow-lg ${
                  convo.unreadCount > 0 ? 'bg-blue-50' : 'bg-white'
                }`}
                data-testid={`conversation-${convo.oderId}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => navigate(`/dm/${convo.oderId}`)}
                    >
                      <div className="w-12 h-12 rounded-full bg-navy-100 flex items-center justify-center overflow-hidden">
                        {convo.otherAvatar ? (
                          <img 
                            src={convo.otherAvatar} 
                            alt={convo.otherName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-6 h-6 text-navy-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className={`font-medium text-navy-900 ${convo.unreadCount > 0 ? 'font-bold' : ''}`}>
                            {convo.otherName}
                          </h3>
                          <span className="text-xs text-gray-400">
                            {formatTime(convo.lastMessageTime)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className={`text-sm truncate ${convo.unreadCount > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                            {convo.lastMessage}
                          </p>
                          {convo.unreadCount > 0 && (
                            <span className="ml-2 min-w-[20px] h-5 bg-navy-900 rounded-full text-white text-xs flex items-center justify-center px-1.5">
                              {convo.unreadCount}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConvoUserId(convo.oderId);
                      }}
                      data-testid={`delete-conversation-${convo.oderId}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )
        ) : (
          // Inbox Tab
          inboxMessages.length === 0 ? (
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardContent className="p-8 text-center">
                <Mail className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg font-medium">Inbox is empty</p>
                <p className="text-gray-400 text-sm mt-2">
                  System notifications will appear here
                </p>
              </CardContent>
            </Card>
          ) : (
            inboxMessages.map((message) => (
              <Card
                key={message.id}
                className={`border-0 shadow-md rounded-xl transition-all ${
                  !message.read ? 'bg-blue-50' : 'bg-white'
                }`}
              >
                <CardContent className="p-4">
                  <div 
                    className="cursor-pointer"
                    onClick={() => toggleExpandMessage(message)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        !message.read ? 'bg-navy-900' : 'bg-gray-200'
                      }`}>
                        <Mail className={`w-5 h-5 ${!message.read ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className={`text-navy-900 ${!message.read ? 'font-bold' : 'font-medium'}`}>
                            {message.subject || 'No Subject'}
                          </h3>
                          <span className="text-xs text-gray-400">
                            {formatTime(message.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          From: {message.senderName}
                        </p>
                        {expandedMessage?.id !== message.id && (
                          <p className="text-sm text-gray-600 mt-1 truncate">
                            {message.content}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded content */}
                  {expandedMessage?.id === message.id && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        <LinkifyText>{message.content}</LinkifyText>
                      </p>
                      <div className="mt-4 flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteMessageId(message.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )
        )}
      </div>

      {/* Delete Message Confirmation Dialog */}
      <AlertDialog open={!!deleteMessageId} onOpenChange={() => setDeleteMessageId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Message</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this message? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMessage}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Conversation Confirmation Dialog */}
      <AlertDialog open={!!deleteConvoUserId} onOpenChange={() => setDeleteConvoUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this entire conversation? All messages will be permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConversation}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Conversation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
};

export default MessagesPage;
